
import { addDays, addMinutes, addMonths, addWeeks, differenceInMilliseconds, endOfDay, endOfMinute, endOfWeek, format, isAfter, isBefore, isSameDay, startOfDay, startOfWeek } from 'date-fns'
import Litepicker from 'litepicker'
import { defineComponent } from 'vue'
import AppointmentTypeService from '../common/appointmentTypeService'
import AppointmentService from '../common/appointmentService'
import TimeService from '../common/timeService'
import { AppointmentType, AppointmentTypeDetail } from '../models/appointmentType'
import AvailabilitySlot from '../models/availabilitySlot'
import { DynamicInput } from '../models/dynamicInput'
import DynamicField from '../components/DynamicField.vue'
import { cloneDeep } from 'lodash'
import AppointmentSlotSelection from './AppointmentSlotSelection.vue'
import SponsorService from '../common/sponsorService'
import { Sponsor } from '../models/sponsor'
import User from '../models/user'
import UserReassign from '../models/userReassign'
import { mapGetters, mapState } from 'vuex'
import { AxiosError } from 'axios'
import IntakeForm from './IntakeForm.vue'
import FormattedIntakeFormResponse from '../models/formattedIntakeFormResponse'

export default defineComponent({
  name: 'AppointmentScheduler',

  props: {
    /** Indicates that a reviewing is scheduling an appointment */
    internalScheduling: {
      type: Boolean,
      default: false
    }
  },

  emits: ['scheduled', 'cancel'],

  data () {
    return {
      isActive: true,

      errorScheduling: false,

      timeNotAvailable: false,

      loadingSponsors: false,

      /** List of available appointment types */
      appointmentTypes: [] as Array<AppointmentTypeDetail>,

      /** List of available times for the selected appointment type */
      // availabilitySlots: [] as Array<AvailabilitySlot>,

      /** List of reviewers for a given time slot */
      availableReviewers: [] as Array<UserReassign>,

      /** Date range to pull availability */
      dateRange: {
        endTime: endOfWeek(new Date()),
        startTime: startOfWeek(new Date())
      },

      /** Information collected to schedule the appointment */
      scheduleForm: {
        appointmentTypeId: 0,
        appointmentTypeOfferingId: 0,
        sponsorId: 0,
        sponsorName: '',
        endTime: null as unknown as Date,
        formResponse: {} as FormattedIntakeFormResponse,
        startTime: null as unknown as Date,
        reviewDeadline: null as unknown as Date,
        reviewerId: null as unknown as number | null
      },

      /** Indicates that the appointment is being scheduled */
      scheduling: false,

      /** Indicates that appointment type will be selected through sponsor */
      showSponsors: true,

      /** List of sponsors */
      sponsors: [] as Array<Sponsor>,

      /** String to search sponsors ons */
      sponsorsSearchKey: '',

      /** The current step of the schedule process */
      step: 1
    }
  },

  mounted () {
    // Get sponsors
    this.loadingSponsors = true
    SponsorService.getAll().then(res => {
      this.sponsors = res.data.sort((a, b) => {
        if (a.toTop > b.toTop) {
          return -1
        } else if (a.toTop < b.toTop) {
          return 1
        } else {
          return 0
        }
      })

      this.loadingSponsors = false
    })

    // Get all appointment types
    if (this.internalScheduling) {
      AppointmentTypeService.getAll().then(res => {
        this.appointmentTypes = res.data.filter(r => !r.removed)
      })
    } else {
      AppointmentTypeService.getPublic().then(res => {
        this.appointmentTypes = res.data
      })
    }
  },

  computed: {
    /** get appointment type for selected sponsor */
    sponsorAppointmentType (): AppointmentTypeDetail {
      return this.appointmentTypes.find(at => at.id === this.selectedSponsor.appointmentTypeId) as AppointmentTypeDetail
    },

    /** returns true if appointment type is a task */
    taskAppointmentTypes (): Array<AppointmentType> {
      return this.appointmentTypes.filter(at => at.isTask)
    },

    /** sort available reviewers by workload */
    sortedAvailableReviewers (): Array<UserReassign> {
      const reviewers = cloneDeep(this.availableReviewers)
        .sort((a, b) => {
          if (a.workload > b.workload) {
            return 1
          } else if (a.workload < b.workload) {
            return -1
          } else {
            return 0
          }
        })

      if (this.isAdmin) {
        return reviewers
      } else {
        return reviewers.filter(r => !r.hasExternalConflicts && r.availableForSlot)
      }
    },

    /** Available Reviewer */
    availableReviewerExists (): boolean {
      if (!this.availableReviewers) {
        return false
      }

      return this.availableReviewers.some(u => !u.hasExternalConflicts && u.availableForSlot)
    },

    ...mapState(['userId']),

    ...mapGetters(['isAdmin']),

    /** filter sponsor listed down to characters typed */
    filteredSponsors (): Array<Sponsor> {
      if (this.sponsorsSearchKey !== '') {
        return this.sponsors.filter(s => RegExp(this.sponsorsSearchKey, 'i').test(s.name))
      } else {
        return this.sponsors
      }
    },

    /** Returns true if the deadline has already passed */
    hasReviewDeadlinePassed (): boolean {
      return isBefore(this.scheduleForm.reviewDeadline, new Date())
    },

    /** returns true if review deadline is today */
    isReviewDeadlineSameDay (): boolean {
      return isSameDay(new Date(), this.scheduleForm.reviewDeadline)
    },

    /** The selected appointment type */
    selectedAppointmentType (): AppointmentTypeDetail {
      return this.appointmentTypes.find(at => at.id === this.scheduleForm.appointmentTypeId) as AppointmentTypeDetail
    },

    /** The selected sponsor */
    selectedSponsor (): Sponsor {
      return this.sponsors.find(s => s.id === this.scheduleForm.sponsorId) as Sponsor
    },

    /** The slots that fall within the current time range */
    // slotsForWeek (): Array<AvailabilitySlot> {
    //   return this.availabilitySlots.filter(slot => {
    //     return isAfter(slot.start, this.dateRange.startTime) && isBefore(slot.start, this.dateRange.endTime)
    //   })
    // },

    /** Dates for the current week */
    weekDays (): Array<Date> {
      return [1, 2, 3, 4, 5].map(x => {
        return addDays(this.dateRange.startTime, x)
      })
    }
  },

  methods: {
    /** cancel action */
    cancel () {
      this.$emit('cancel')
    },

    /** returns true if given date is six months or more out */
    isSixMonthsOut (date: Date) {
      return isAfter(date, addMonths(new Date(), 6))
    },

    /** sets reviewer chosen */
    selectReviewer (userId: number | null) {
      this.scheduleForm.reviewerId = userId
      this.step = 4
    },

    /**
     * workload in hours conversion
     */
    workloadInHours (workload: number): string {
      const hours = Math.floor(workload / 60)
      const minutes = workload - (hours * 60)
      return `${hours > 0 ? hours : '0'}${hours === 1 ? 'hr' : 'hrs'} ${minutes}min`
    },

    /**
     * Moves the form back one step
     */
    back () {
      if (this.step === 1) {
        this.step += 1
        this.scheduleForm.sponsorId = 0
      } else if (this.step === 2) {
        if (this.internalScheduling) {
          this.scheduleForm.appointmentTypeId = 0
        } else {
          this.scheduleForm.appointmentTypeId = 0
          this.scheduleForm.sponsorId = 0
        }
      } else if (this.step === 3) {
        this.showDeadlineCalendar()
        this.scheduleForm.reviewDeadline = null as unknown as Date
        // this.availabilitySlots = []
      } else if (this.step === 4) {
        this.scheduleForm.appointmentTypeOfferingId = 0
        this.scheduleForm.startTime = null as unknown as Date
        this.scheduleForm.endTime = null as unknown as Date
        if (this.selectedAppointmentType.isTask) {
          this.showReviewDateCalendar()
        }
      }

      this.step -= 1
    },

    backToTime () {
      this.scheduleForm.startTime = null as unknown as Date
      this.scheduleForm.endTime = null as unknown as Date
      this.availableReviewers = []
    },

    /**
     * Formates the given number of minutes into a human readable time
     */
    formatMinutes (minutes: number): string {
      return TimeService.minutesToHoursFormatted(minutes)
    },

    /**
     * Formats the given date in the specified format
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    /**
     * Fetches availability for the selected appointment type
     * and date range
     */
    // getAvailability () {
    //   const start = new Date()
    //   const end = this.scheduleForm.reviewDeadline
    //   AppointmentTypeService.getAvailability(this.scheduleForm.appointmentTypeId, start, end, false).then(res => {
    //     res.data.forEach(d => {
    //       d.start = new Date(d.start)
    //       d.end = new Date(d.end)
    //     })

    //     this.dateRange.startTime = startOfWeek(res.data[0].start)
    //     this.dateRange.endTime = endOfWeek(this.dateRange.startTime)

    //     this.availabilitySlots = res.data
    //   })
    // },

    /**
     * returns true if user can go to confirmation page
     */
    goToConfirm () {
      const el = document.querySelector('.system-settings.page')
      if (el) {
        el.scrollTop = 0
      }

      this.step = 5
    },

    /**
     * Moves the date range forward one week
     */
    nextWeek () {
      this.dateRange.startTime = addWeeks(this.dateRange.startTime, 1)
      this.dateRange.endTime = addWeeks(this.dateRange.endTime, 1)
    },

    /**
     * Moves the date range back one week
     */
    previousWeek () {
      this.dateRange.startTime = addWeeks(this.dateRange.startTime, -1)
      this.dateRange.endTime = addWeeks(this.dateRange.endTime, -1)
    },

    /**
     * Schedules the appointment with the given information
     */
    schedule () {
      this.scheduling = true
      this.isActive = false

      const params = {
        appointmentTypeId: this.scheduleForm.appointmentTypeId,
        appointmentTypeOfferingId: this.scheduleForm.appointmentTypeOfferingId,
        startTime: this.scheduleForm.startTime.toISOString(),
        intakeFormResponse: this.scheduleForm.formResponse.intakeFormResponses,
        reviewDeadline: this.scheduleForm.reviewDeadline.toISOString(),
        reviewerId: this.scheduleForm.reviewerId,
        sponsorId: this.scheduleForm.sponsorId,
        projectUserId: this.userId,
        additionalContacts: this.scheduleForm.formResponse.additionalContacts
      }

      const globalResponses = cloneDeep(Object.values(this.scheduleForm.formResponse.defaultResponses))
      globalResponses.forEach(gr => {
        params[gr.id] = gr.value
      })

      AppointmentService.scheduleAppointment(params).then(res => {
        this.step = 6
        this.$emit('scheduled')
        this.scheduling = false
      }).catch((err: AxiosError) => {
        (window as any).bad = err
        if (err.response?.status === 409) {
          // this.getAvailability()
          this.scheduling = false
          this.timeNotAvailable = true
        } else {
          this.errorScheduling = true
        }
      })
      this.isActive = true
    },

    /**
     * Handles the selection of an appointment type
     * @param appointmentType: The selected appointment type
     */
    selectAppointmentType (appointmentTypeId: number) {
      this.scheduleForm.appointmentTypeId = appointmentTypeId
      this.showDeadlineCalendar()
      // this.resetIntakeFormResponse()
      this.step = 2
    },

    /**
     * Handles the selection of a time slot
     * @param slot: The time slot
     */
    selectSlot (slot: AvailabilitySlot) {
      this.scheduleForm.appointmentTypeOfferingId = slot.appointmentTypeOffering.id
      this.scheduleForm.startTime = slot.start
      this.scheduleForm.endTime = addMinutes(slot.start, this.selectedAppointmentType.duration)
      if (this.internalScheduling) {
        AppointmentService.reviewerAvailability(slot.appointmentTypeOffering.id, slot.start).then(res => {
          this.availableReviewers = res.data
        })
        // this.scheduleForm.reviewerId = slot.reviewer.id
      } else {
        this.step = 4
      }
    },

    /**
     * Selects the sponsor and sets the appointment
     * @params sponsor: The given sponsor
     */
    selectSponsor (sponsor: Sponsor) {
      this.scheduleForm.sponsorId = sponsor.id
      if (!this.internalScheduling) {
        this.selectAppointmentType(sponsor.appointmentTypeId)
      }
    },

    // /**
    //  * Returns the slots available on the given day
    //  * @param day: The day in the form of a Date object
    //  */
    // slotsForDay (day: Date): Array<AvailabilitySlot> {
    //   return this.slotsForWeek.filter(slot => {
    //     return slot.start.getDay() === day.getDay()
    //   })
    // },

    /** shows calendar to pick deadline date */
    showDeadlineCalendar () {
      setTimeout(() => {
        const picker = new Litepicker({ element: document.querySelector('.deadline-picker'), inlineMode: true, singleMode: true, onSelect: this.handleDeadlineSelection })
        picker.show()
      }, 0)
    },

    /** shows calendar for review date selection */
    showReviewDateCalendar () {
      setTimeout(() => {
        const picker = new Litepicker({ element: document.querySelector('.review-date-picker'), inlineMode: true, singleMode: true, onSelect: this.handleReviewDateSelection, disableWeekends: true, minDate: startOfDay(new Date()), maxDate: endOfDay(this.scheduleForm.reviewDeadline) })
        picker.show()
      }, 0)
    },

    /** handle reviewers available for date */
    handleReviewDateSelection (date: Date) {
      this.scheduleForm.startTime = startOfDay(date)
      this.scheduleForm.endTime = endOfDay(date)

      if (!this.internalScheduling) {
        this.step = 4
      } else {
        AppointmentService.reviewerAvailabilityForDate(date).then(res => {
          this.availableReviewers = res.data
        })
      }
    },

    /** handle date selection and deadline date */
    handleDeadlineSelection (date: Date) {
      this.scheduleForm.reviewDeadline = date
      if (!this.selectedAppointmentType.isTask) {
        // this.getAvailability()
      } else {
        this.showReviewDateCalendar()
      }
      this.step = 3
    }
  },

  components: {
    // DynamicField,
    IntakeForm,
    AppointmentSlotSelection
  }
})
